import { apiFetch, snakeToCamel } from '@/core/api/client'

export interface AgentConfig {
  id: string
  agentId: string
  name: string
  description: string
  enabled: boolean
  autonomyLevel: 'disabled' | 'assist' | 'human_in_loop' | 'autonomous'
  config: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

/** Extended config fields we store inside config JSON. */
export interface AgentSkills {
  sendEmails: boolean
  readEmails: boolean
  modifyCrm: boolean
  readCrm: boolean
  scheduleSessions: boolean
  generateReports: boolean
  sendNotifications: boolean
}

export const DEFAULT_SKILLS: AgentSkills = {
  sendEmails: true,
  readEmails: true,
  modifyCrm: true,
  readCrm: true,
  scheduleSessions: false,
  generateReports: false,
  sendNotifications: true,
}

export const SKILL_LABELS: Record<keyof AgentSkills, { label: string; description: string }> = {
  sendEmails: { label: 'Send Emails', description: 'Compose and send emails to customers' },
  readEmails: { label: 'Read Emails', description: 'Access inbound emails and conversation threads' },
  modifyCrm: { label: 'Modify CRM', description: 'Create contacts, add notes, update pipeline stages' },
  readCrm: { label: 'Read CRM', description: 'Search contacts and view customer history' },
  scheduleSessions: { label: 'Schedule Sessions', description: 'Book training sessions and manage calendar' },
  generateReports: { label: 'Generate Reports', description: 'Create summaries and analytics reports' },
  sendNotifications: { label: 'Send Notifications', description: 'Alert admins about important events' },
}

export interface AgentRun {
  id: string
  agentId: string
  trigger: 'chat' | 'scheduled' | 'signal' | 'webhook'
  actionType: 'email_draft' | 'contact_note' | 'stage_suggestion' | 'schedule_suggestion' | 'report_summary' | 'general'
  status: 'pending' | 'completed' | 'failed' | 'approved' | 'rejected'
  riskLevel: 'low' | 'medium' | 'high' | 'critical'
  inputData: Record<string, unknown>
  outputData: Record<string, unknown>
  executedTools: unknown[]
  approvedBy: string | null
  approvedAt: string | null
  createdAt: string
}

/** Fetch all agent configs. */
export async function getAgentConfigs(): Promise<AgentConfig[]> {
  const res = await apiFetch('/ai/configs/')
  if (!res.ok) {throw new Error('Failed to fetch agent configs')}
  const data = await res.json()
  return (data.results || data).map((r: unknown) => snakeToCamel<AgentConfig>(r))
}

/** Update an agent config. */
export async function updateAgentConfig(agentId: string, data: Partial<AgentConfig>): Promise<AgentConfig> {
  const payload: Record<string, unknown> = {}
  if (data.enabled !== undefined) {payload.enabled = data.enabled}
  if (data.autonomyLevel !== undefined) {payload.autonomy_level = data.autonomyLevel}
  if (data.config !== undefined) {payload.config = data.config}
  if (data.name !== undefined) {payload.name = data.name}
  if (data.description !== undefined) {payload.description = data.description}

  const res = await apiFetch(`/ai/configs/${agentId}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {throw new Error('Failed to update agent config')}
  return snakeToCamel<AgentConfig>(await res.json())
}

/** Fetch agent runs, optionally filtered by agent_id and status. */
export async function getAgentRuns(params?: { status?: string; agentId?: string }): Promise<AgentRun[]> {
  const query = new URLSearchParams()
  if (params?.status) {query.set('status', params.status)}
  if (params?.agentId) {query.set('agent_id', params.agentId)}
  const res = await apiFetch(`/ai/runs/?${query.toString()}`)
  if (!res.ok) {throw new Error('Failed to fetch agent runs')}
  const data = await res.json()
  return (data.results || data).map((r: unknown) => snakeToCamel<AgentRun>(r))
}

/** Approve a pending agent run. */
export async function approveAgentRun(id: string): Promise<AgentRun> {
  const res = await apiFetch(`/ai/runs/${id}/approve/`, { method: 'POST' })
  if (!res.ok) {throw new Error('Failed to approve run')}
  return snakeToCamel<AgentRun>(await res.json())
}

/** Reject a pending agent run. */
export async function rejectAgentRun(id: string): Promise<AgentRun> {
  const res = await apiFetch(`/ai/runs/${id}/reject/`, { method: 'POST' })
  if (!res.ok) {throw new Error('Failed to reject run')}
  return snakeToCamel<AgentRun>(await res.json())
}

/** Get pending approval count and agent status. */
export async function getAgentStatus(): Promise<{ pendingApprovals: number; agents: Array<Pick<AgentConfig, 'agentId' | 'name' | 'enabled' | 'autonomyLevel'>> }> {
  const res = await apiFetch('/ai/tasks/')
  if (!res.ok) {throw new Error('Failed to fetch agent status')}
  const data = await res.json()
  return {
    pendingApprovals: data.pending_approvals,
    agents: data.agents.map((a: Record<string, unknown>) => snakeToCamel(a)),
  }
}
